import { apiEndpoints } from '@/apiEndpoints';
import { Benefit } from '@/types/benefits/benefitColumnDef';
import type { BenefitUsageResponse } from '@/types/benefits/circuitsUsedByBenefitsColumnDef';
import { BenefitFormData } from '@/types/circuits/CircuitDaysFormData';
import { Draft } from '@/types/dashboard/draftColumnDef';
import axios from 'axios';

interface BenefitsResponse {
  total: number;
  benefits: Benefit[];
}

export interface BenefitParams {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  filters?: Record<string, string>;
}

export const getBenefits = async ({
  page = 1,
  pageSize = 10,
  sortField = '',
  sortOrder = 'asc',
  filters = {},
}: BenefitParams) => {
  const filtersStr = Object.entries(filters)
    .map(([key, value]) => `${key}:${encodeURIComponent(value)}`)
    .join(',');
  const result = await axios.get<BenefitsResponse>(apiEndpoints.benefits.base, {
    params: {
      page,
      pageSize,
      sortField,
      sortOrder,
      filters: filtersStr,
    },
  });
  return result.data;
};

export const deleteBenefit = async (benefitId: string) => {
  const result = await axios.delete(apiEndpoints.benefits.detail(benefitId));
  return result.data;
};

export const deleteMultipleBenefits = async (benefitIds: string[]) => {
  const result = await axios.delete(apiEndpoints.benefits.multiple, {
    data: { benefitIds },
  });
  return result.data;
};

export const duplicateBenefit = async (benefitId: string, fields: string[]) => {
  const result = await axios.post(apiEndpoints.benefits.duplicate(benefitId), {
    fields,
  });
  return result.data;
};

export const getDraftsBenefits = async () => {
  const result = await axios.get<Draft[]>(apiEndpoints.benefits.drafts);
  return result.data;
};

export type CountriesWithBenefits = Array<{ country: string }>;

export const getCountriesWithBenefits = async () => {
  const result = await axios.get<CountriesWithBenefits>(
    apiEndpoints.benefits.countries,
  );
  return result.data;
};

export type TypesWithBenefits = string[];

export const getTypesWithBenefits = async () => {
  const result = await axios.get<TypesWithBenefits>(
    apiEndpoints.benefits.types,
  );
  return result.data;
};

export type SuppliersWithBenefits = Array<{ name: string; id: string }>;

export const getSuppliersWithBenefits = async () => {
  const result = await axios.get<SuppliersWithBenefits>(
    apiEndpoints.benefits.suppliers,
  );
  return result.data;
};

export interface PublishedBenefitsParams {
  types: string[];
  activityType?: string;
  name?: string;
  hotelClassifications?: string[];
  hotelCity?: string;
  countries?: string[];
}

export type PublishedBenefitsResponse = Record<string, BenefitFormData[]>;

export const getPublishedBenefits = async ({
  types,
  activityType,
  name,
  hotelClassifications = [],
  hotelCity,
  countries = [],
}: PublishedBenefitsParams) => {
  const typesStr = types.join(',');
  const hotelClassificationsStr = hotelClassifications.join(',');
  const countriesStr = countries.join(',');
  const result = await axios.get<PublishedBenefitsResponse>(
    apiEndpoints.benefits.published,
    {
      params: {
        types: typesStr,
        activityType,
        name,
        hotelClassifications: hotelClassificationsStr,
        hotelCity,
        countries: countriesStr,
      },
    },
  );
  return result.data;
};

export type CitiesResponse = {
  city: string;
}[];

export const getBenefitsCities = async (country?: string) => {
  const result = await axios.get<CitiesResponse>(apiEndpoints.benefits.cities, {
    params: country ? { country } : undefined,
  });
  return result.data;
};

export type HotelAssociatedBenefitItem = {
  id: string;
  name: string | null;
  code: number | null;
  type: string;
  country: string | null;
  supplier: { id: string; name: string | null } | null;
  activity: { activityType: string; city: string | null } | null;
};

export type HotelAssociatedBenefitsResponse = {
  benefits: HotelAssociatedBenefitItem[];
  filters: {
    suppliers: Array<{ id: string; name: string | null }>;
    countries: Array<{ country: string }>;
    activityTypes: Array<string>;
  };
};

export interface HotelAssociatedBenefitsParams {
  name?: string;
  supplierId?: string;
  country?: string;
  activityType?: string;
}

export const getHotelAssociatedBenefits = async ({
  name,
  supplierId,
  country,
  activityType,
}: HotelAssociatedBenefitsParams) => {
  const result = await axios.get<HotelAssociatedBenefitsResponse>(
    apiEndpoints.benefits.hotelAssociated,
    {
      params: {
        name: name || undefined,
        supplierId: supplierId || undefined,
        country: country || undefined,
        activityType: activityType || undefined,
      },
    },
  );
  return result.data;
};

export const getCircuitsUsingBenefit = async (
  benefitId: string,
): Promise<BenefitUsageResponse> => {
  const result = await axios.get<BenefitUsageResponse>(
    apiEndpoints.benefits.circuits(benefitId),
  );
  return result.data;
};
